import React, { useCallback } from 'react';

import { EntreeVehiculeViolationDtoFormFieldEnum } from 'lib_api/lib/api/gen';

import BaseDateTimePicker from 'components/BaseForm/BaseDateTimePicker';
import AsyncSelect from 'components/BaseForm/Select/AsyncSelect';
import { FormInputProps } from 'components/BaseForm/types';
import { useApi } from 'hooks/ApiStoreContext';
import { useDossierContext } from 'hooks/dossiers/DossierContext';
import { useEnvironnement } from 'hooks/EnvironementStoreContext';
import { useUserStore } from 'hooks/UserStoreContext';
import {
  fetchAutoriteFourriereOfFourriere,
  fetchValidAutoriteFourriere,
} from 'search/searchAutoriteFourriere';
import { extractNonnullDossierIdOrThrow } from 'utils/dossierUtils';
import { formatAutoriteFourriereDto } from 'utils/dtoToString';

import { EntreVehiculeFormValues } from './types';
import {
  convertEntreeVehiculeToEntreeVehiculeRequestDto,
  removeRepeatedAf,
  useSubmitEntreeVehicule,
} from './utils';
import WithJusticeEntreeVehiculeForm from './WithJusticeEntreeVehiculeForm';
import WithoutJusticeEntreeVehiculeForm from './WithoutJusticeEntreeVehiculeForm';

interface EntreeVehiculeProps {
  close: () => void;
}

const EntreeVehiculeForm: React.FC<EntreeVehiculeProps> = ({
  close,
}: EntreeVehiculeProps): React.ReactElement => {
  const user = useUserStore();
  const [{ result: dossier }] = useDossierContext();
  const dossierId = extractNonnullDossierIdOrThrow(dossier);
  const { GardienFourriereControllerApi, ReferentielControllerApi } = useApi();
  const [{ JUSTICE_ACTIVE_BOOL }] = useEnvironnement();

  const userIdFourriere = user.idCorrelationFourriere;
  if (!userIdFourriere) {
    throw new Error('id correlation fourrière should be defined');
  }

  const { inProgress, submit } = useSubmitEntreeVehicule(close);

  const validateField = useCallback(
    async (
      field: EntreeVehiculeViolationDtoFormFieldEnum,
      formValues: EntreVehiculeFormValues,
    ) => {
      const response =
        await GardienFourriereControllerApi.validateEntreeVehiculeFormFieldUsingPOST(
          field,
          dossierId,
          convertEntreeVehiculeToEntreeVehiculeRequestDto(formValues),
        );

      const violations = response.entreeVehiculeViolationDtoList ?? [];
      if (violations.length === 0) {
        return Promise.resolve();
      }

      return Promise.reject(violations[0].message);
    },
    [GardienFourriereControllerApi, dossierId],
  );

  const immatriculation =
    dossier.body?.ficheVehicule?.immatriculation?.numero || undefined;

  const selectedAfByFo =
    dossier.body?.ficheInfraction?.idCorrelationAutoriteFourriereFicheFo || '';

  const branche = dossier.body?.ficheInfraction?.motifMef?.branche;

  const AutoriteFourriereSelectInput: FormInputProps<
    EntreVehiculeFormValues,
    EntreeVehiculeViolationDtoFormFieldEnum
  > = {
    field:
      EntreeVehiculeViolationDtoFormFieldEnum.ID_CORRELATION_AUTORITE_FOURRIERE,
    required: true,
    name: 'idCorrelationAutoriteFourriere',
    //dependencie ajoutée pour déclencher une validation avant la soumission de l'AF saisie dans BdR
    dependencies: ['dateEnlevement'],
    label: 'Autorité de fourrière',
    render: () => {
      return (
        <AsyncSelect
          fetchOptions={() => {
            return Promise.all([
              fetchValidAutoriteFourriere(
                ReferentielControllerApi,
                selectedAfByFo,
              ),
              fetchAutoriteFourriereOfFourriere(
                ReferentielControllerApi,
                userIdFourriere,
              ),
            ]).then(results => removeRepeatedAf(results.flat()));
          }}
          getOptionLabel={autorite => formatAutoriteFourriereDto(autorite)}
          getOptionValue={autorite => autorite.idCorrelation}
        />
      );
    },
  };

  const baseInputs: FormInputProps<
    EntreVehiculeFormValues,
    EntreeVehiculeViolationDtoFormFieldEnum
  >[] = [
    {
      field: EntreeVehiculeViolationDtoFormFieldEnum.DATE_ENLEVEMENT,
      required: true,
      name: 'dateEnlevement',
      dependencies: ['dateEntree'],
      label: "Date et heure d'enlèvement",
      render: () => {
        return <BaseDateTimePicker />;
      },
    },
    {
      field: EntreeVehiculeViolationDtoFormFieldEnum.DATE_ENTREE,
      required: true,
      name: 'dateEntree',
      dependencies: ['dateEnlevement'],
      label: "Date et heure d'entrée sur le parc",
      render: () => {
        return <BaseDateTimePicker />;
      },
    },
  ];

  return (
    <>
      <h3 className={'mainTitle'}>
        {`Enregistrer l'entrée du véhicule ${immatriculation || ''}`}
      </h3>
      {JUSTICE_ACTIVE_BOOL ? (
        <WithJusticeEntreeVehiculeForm
          close={close}
          dossier={dossier}
          inProgress={inProgress}
          validateField={validateField}
          submit={submit}
          branche={branche}
          autoriteFourriereSelectInput={AutoriteFourriereSelectInput}
          baseInputs={baseInputs}
        />
      ) : (
        <WithoutJusticeEntreeVehiculeForm
          close={close}
          dossier={dossier}
          inProgress={inProgress}
          validateField={validateField}
          submit={submit}
          autoriteFourriereSelectInput={AutoriteFourriereSelectInput}
          baseInputs={baseInputs}
        />
      )}
    </>
  );
};

export default EntreeVehiculeForm;
